import { useState } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Flag, Loader2, AlertTriangle, ShieldCheck, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/site/Reveal";
import { API, SITE } from "@/lib/site";
import useSeo from "@/hooks/useSeo";

const REPORT_CATEGORIES = [
  "Safety concern", "Harassment", "Threat", "Fake profile", "Impersonation",
  "Scam or fraud", "Location or privacy", "Photo Verification", 
  "Business conduct", "Event issue", "Illegal activity", "Copyright complaint",
  "Privacy request", "Other",
];

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Enter a valid email"),
  category: z.string().min(1, "Please choose a category"),
  subject: z.string().min(3, "Add a short summary"),
  description: z.string().min(10, "Please describe what happened (at least 10 characters)"),
  privacy_ack: z.literal(true, { errorMap: () => ({ message: "Please acknowledge the privacy notice" }) }),
});

const field = "w-full rounded-xl border border-input bg-white px-4 py-3 text-navy placeholder:text-bodygrey/50 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/30";

export default function Report() {
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);
  const [emailSent,setEmailSent]=useState(false);
  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({ resolver: zodResolver(schema) });

  useSeo({
    title: "Report a Concern — Orrbbit",
    description: "Report a safety concern, harassment, fake profile, scam, event issue or other problem to Orrbbit.",
    path: "/report",
  });

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/support/contact`, { ...data, account_email: null });
      setReference(res.data.reference);setEmailSent(!!res.data.email_sent);
      toast.success("Report received", { description: `Reference ${res.data.reference}. ${res.data.email_sent ? "Check your inbox for a confirmation." : "Your report is saved. Email confirmation is currently unavailable."}` });
      reset();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error("Couldn't send report", { description: typeof detail === "string" ? detail : `Please try again or email ${SITE.supportEmail}.` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="report-page">
      <section className="border-b border-border bg-lightbg">
        <div className="container-x section-space">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-orange">Report a Concern</span>
            <h1 className="mt-3 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">Tell us what's wrong</h1>
            <p className="mt-4 max-w-2xl text-lg text-bodygrey">
              Report a safety concern, harassment, a fake profile, a scam, an event issue or anything else that
              shouldn't be on Orrbbit. Every report is reviewed under our{" "}
              <Link to="/moderation-appeals" className="font-semibold text-teal hover:underline">Moderation, Reports &amp; Appeals Policy</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-x py-14">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal className="mb-8">
              <div className="flex items-start gap-3 rounded-2xl border border-orange/30 bg-orange/5 p-5" data-testid="report-emergency-notice">
                <AlertTriangle className="mt-0.5 flex-none text-orange" size={20} />
                <p className="text-sm text-navy">Orrbbit support is not an emergency service. If you or someone else is in immediate danger, contact your local emergency services first.</p>
              </div>
            </Reveal>

            {reference && (
              <Reveal className="mb-8">
                <div data-testid="report-success" className="rounded-2xl border border-teal/30 bg-teal/5 p-5 text-navy">
                  <p className="font-semibold">Thanks — your report has been received.</p>
                  <p className="mt-1 text-sm">Your reference number is <span className="font-mono font-semibold text-teal" data-testid="report-reference">{reference}</span>. {emailSent?"We’ve emailed you a confirmation.":"Your concern is saved. Confirmation delivery is pending; keep this reference."}</p>
                </div>
              </Reveal>
            )}

            <Reveal>
              <form onSubmit={handleSubmit(onSubmit)} data-testid="report-form" className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="report-name-input" className="mb-1.5 block text-sm font-medium text-navy">Name</label>
                    <input id="report-name-input" data-testid="report-name-input" className={field} placeholder="Your name" {...register("name")} />
                    {errors.name && <p className="mt-1.5 text-xs text-red-500">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label htmlFor="report-email-input" className="mb-1.5 block text-sm font-medium text-navy">Email</label>
                    <input id="report-email-input" data-testid="report-email-input" className={field} placeholder="you@email.com" {...register("email")} />
                    {errors.email && <p className="mt-1.5 text-xs text-red-500">{errors.email.message}</p>}
                  </div>
                </div>
                <div>
                  <label htmlFor="report-category" className="mb-1.5 block text-sm font-medium text-navy">What are you reporting?</label>
                  <Controller
                    control={control}
                    name="category"
                    render={({ field: f }) => (
                      <Select onValueChange={f.onChange} value={f.value}>
                        <SelectTrigger id="report-category" aria-label="What are you reporting?" data-testid="report-category-select" className="h-[46px] rounded-xl border-input bg-white text-navy"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                        <SelectContent>
                          {REPORT_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`report-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.category && <p className="mt-1.5 text-xs text-red-500">{errors.category.message}</p>}
                </div>
                <div>
                  <label htmlFor="report-subject-input" className="mb-1.5 block text-sm font-medium text-navy">Short summary</label>
                  <input id="report-subject-input" data-testid="report-subject-input" className={field} placeholder="e.g. Fake profile at Saturday's event" {...register("subject")} />
                  {errors.subject && <p className="mt-1.5 text-xs text-red-500">{errors.subject.message}</p>}
                </div>
                <div>
                  <label htmlFor="report-description-input" className="mb-1.5 block text-sm font-medium text-navy">What happened?</label>
                  <textarea id="report-description-input" data-testid="report-description-input" rows={5} className={`${field} resize-none`} placeholder="Include any details that help us investigate — profile names, event names, dates." {...register("description")} />
                  {errors.description && <p className="mt-1.5 text-xs text-red-500">{errors.description.message}</p>}
                </div>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-lightbg p-4">
                  <input type="checkbox" data-testid="report-privacy-ack" className="mt-1 h-4 w-4 accent-teal" {...register("privacy_ack")} />
                  <span className="text-sm text-bodygrey">I understand my details will be used to investigate and respond to this report in line with the <a href="/privacy" className="font-medium text-teal hover:underline">Privacy Policy</a>.</span>
                </label>
                {errors.privacy_ack && <p className="-mt-3 text-xs text-red-500">{errors.privacy_ack.message}</p>}
                <button type="submit" disabled={loading} data-testid="report-submit-btn" className="inline-flex items-center gap-2 rounded-full bg-orange px-8 py-3 font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}{loading ? "Sending..." : "Submit report"}
                </button>
              </form>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal>
              <div className="rounded-2xl border border-border bg-lightbg p-6">
                <p className="flex items-center gap-2 font-heading text-lg font-semibold text-navy"><ShieldCheck size={18} className="text-teal" /> What happens next</p>
                <ul className="mt-3 space-y-2 text-sm text-bodygrey">
                  <li>• You'll receive a confirmation email with a reference number.</li>
                  <li>• Our team reviews reports and may take action under the <Link to="/community-guidelines" className="font-semibold text-teal hover:underline">Community Guidelines</Link>.</li>
                  <li>• Possible outcomes include content removal, warnings, verification removal, suspension or a permanent ban.</li>
                  <li>• You can also report profiles, events and messages directly in the app.</li>
                  <li>• Prefer email? Write to us at <a href={`mailto:${SITE.supportEmail}`} className="font-semibold text-teal hover:underline">{SITE.supportEmail}</a>.</li>
                </ul>
              </div>
            </Reveal>
            <Reveal className="mt-6">
              <div className="flex items-start gap-3 rounded-2xl border border-border bg-white p-5 shadow-soft">
                <Flag size={18} className="mt-0.5 flex-none text-orange" />
                <p className="text-sm text-bodygrey">In danger right now? Contact your local emergency services. Then read our <Link to="/safety" className="font-semibold text-teal hover:underline">Safety Centre</Link>.</p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}

